<template>
  <v-card
    elevation="4"
    rounded="xl"
    class="booking-card overflow-hidden"
  >
    <!-- HERO IMAGE + OVERLAY -->
    <div class="hero-wrapper">
      <v-img
        v-if="imageUrl"
        :src="imageUrl"
        cover
        class="hero-img"
      >
        <template #placeholder>
          <v-skeleton-loader type="image" class="h-100" />
        </template>
      </v-img>

      <template v-else>
        <v-skeleton-loader type="image" class="hero-img" />
      </template>

      <!-- Gradient overlay text -->
      <div class="hero-overlay">
        <!-- Ship + start date -->
        <div
          v-if="shipAndDateLine"
          class="hero-topline text-body-1 text-uppercase mb-4"
        >
          {{ shipAndDateLine }}
        </div>

        <!-- Main title -->
        <h2 class="hero-title">
          {{ listingTitle }}
        </h2>

        <!-- Departure port -->
        <div
          v-if="departurePortLine"
          class="hero-depart text-body-2 text-uppercase mt-4"
        >
          From: {{ departurePortLine }}
        </div>

        <!-- Visiting ports -->
        <div
          v-if="visitingText"
          class="hero-visiting text-body-2 mt-3"
        >
          {{ visitingText }}
        </div>
      </div>

      <!-- Days to go badge -->
      <div v-if="daysToGo !== null" class="days-badge">
        <div class="days-number">{{ daysToGo }}</div>
        <div class="days-label">Days to go</div>
      </div>

      <!-- Status chip -->
      <v-chip
        v-if="statusLabel"
        :color="statusBg(statusRaw)"
        :text-color="statusFg(statusRaw)"
        size="large"
        variant="flat"
        label
        class="status-chip"
      >
        {{ statusLabel }}
      </v-chip>
    </div>

    <!-- SINGLE FOOTER AREA (no nested card) -->
    <div class="footer-wrapper">
      <div class="footer-inner">
        <div class="footer-left">
          <div class="text-caption text-uppercase text-medium-emphasis mb-1">
            Reservation
          </div>
          <div class="text-h4 font-weight-bold mb-4">
            {{ bookingDisplayRef }}
          </div>

          <div class="text-caption text-uppercase text-medium-emphasis mb-1">
            Status
          </div>
          <div class="text-body-1">
            {{ statusLabel }}
          </div>
        </div>

        <div class="footer-right">
          <v-btn
            color="primary"
            size="large"
            class="plan-btn text-uppercase"
            @click="handleView"
          >
            Manage Booking
          </v-btn>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import { supabase } from '@/supabaseClient'

export default {
  name: 'BookingCardVuetify',
  props: {
    booking: { type: Object, required: true },
    isAdmin: { type: Boolean, default: false }
  },
  data() {
    return {
      listing: null,
      imageUrl: null,
      imageLoading: true
    }
  },
  computed: {
    // ----- MAIN DISPLAY FIELDS -----
    listingTitle() {
      return (
        this.listing?.itinerary?.title ||
        this.listing?.title ||
        'Your upcoming voyage'
      )
    },

    shipName() {
      return (
        this.listing?.itinerary?.ship_name ||
        this.listing?.ship_name ||
        ''
      )
    },

    sailingDateStr() {
      const it = this.listing?.itinerary || {}
      const raw =
        it.start_date || it.date || it.departure_date || it.sail_date || null
      if (!raw) return ''
      const dt = new Date(raw)
      if (isNaN(dt)) return ''
      return dt.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },

    shipAndDateLine() {
      if (!this.shipName && !this.sailingDateStr) return ''
      if (!this.shipName) return this.sailingDateStr
      if (!this.sailingDateStr) return this.shipName.toUpperCase()
      return `${this.shipName.toUpperCase()} | ${this.sailingDateStr}`
    },

    departurePortLine() {
      const dep = this.listing?.itinerary?.departure_port
      return dep ? String(dep).toUpperCase() : ''
    },

    portStops() {
      const p = this.listing?.itinerary?.ports || this.listing?.ports || []
      return Array.isArray(p) ? p : []
    },

    visitingText() {
      if (!this.portStops.length) return ''
      const names = this.portStops
        .map((s) => this.formatPort(s))
        .filter(Boolean)
      return names.join(' | ')
    },

    bookingDisplayRef() {
      return (
        this.listing?.booking_id ||
        this.booking?.reference ||
        this.booking?.id ||
        '—'
      )
    },

    // ----- STATUS -----
    statusRaw() {
      return this.listing?.status || this.booking?.status || 'pending'
    },
    statusLabel() {
      return this.displayStatus(this.statusRaw)
    },

    // ----- COUNTDOWN -----
    daysToGo() {
      const it = this.listing?.itinerary || {}
      const raw =
        it.start_date || it.date || it.departure_date || it.sail_date || null

      if (!raw) return null
      const target = new Date(raw)
      if (isNaN(target)) return null

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const trip = new Date(target)
      trip.setHours(0, 0, 0, 0)

      const diffMs = trip - today
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
      return diffDays >= 0 ? diffDays : 0
    }
  },
  async mounted() {
    if (!this.booking?.listing_id) return

    // 1) fetch listing
    const { data: listingData, error: listingError } = await supabase
      .from('listings')
      .select('*')
      .eq('id', this.booking.listing_id)
      .single()

    if (listingError) {
      console.error('Failed to fetch listing for booking:', listingError)
      this.imageLoading = false
      return
    }
    this.listing = listingData

    // 2) fetch hero image based on departure port
    const port = this.listing?.itinerary?.departure_port
    if (!port) {
      this.imageLoading = false
      return
    }

    const portPath = `${String(port).toLowerCase()}.jpg`
    const { data } = supabase.storage.from('locations').getPublicUrl(portPath)
    this.imageUrl = data?.publicUrl || null
    this.imageLoading = false
  },
  methods: {
    displayStatus(s) {
      const map = {
        pending: 'Pending',
        confirmed: 'Confirmed',
        cancelled: 'Confirmed',
        canceled: 'Confirmed',
        active: 'Active',
        bookingpending: 'Booking Pending',
        paid: 'Paid in Full'
      }
      return map[(s || '').toLowerCase()] || 'Pending'
    },
    statusBg(s) {
      const key = (s || '').toLowerCase()
      return (
        {
          pending: 'amber-lighten-4',
          bookingpending: 'amber-lighten-4',
          confirmed: 'green-lighten-4',
          active: 'blue-lighten-4',
          cancelled: 'green-lighten-4',
          canceled: 'green-lighten-4',
          paid: 'green-lighten-4'
        }[key] || 'amber-lighten-4'
      )
    },
    statusFg(s) {
      const key = (s || '').toLowerCase()
      return (
        {
          pending: 'amber-darken-4',
          bookingpending: 'amber-darken-4',
          confirmed: 'green-darken-4',
          active: 'blue-darken-4',
          cancelled: 'green-darken-4',
          canceled: 'green-darken-4',
          paid: 'green-darken-4'
        }[key] || 'amber-darken-4'
      )
    },
    formatPort(stop = {}) {
      if (typeof stop === 'string') return stop
      return stop.name || stop.port || stop.city || stop.country || ''
    },
    basePath() {
      const id = this.booking?.id
      if (!id) return null
      return `/dashboard/bookings/${id}`
    },
    handleView() {
      const path = this.basePath()
      if (!path) return
      this.$router.push(path)
    }
  }
}
</script>

<style scoped>
.booking-card {
  background-color: #ffffff;
}

/* HERO AREA */
.hero-wrapper {
  position: relative;
}

.hero-img {
  height: 300px;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #ffffff;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.6) 100%
  );
}

.hero-topline {
  letter-spacing: 0.12em;
  font-weight: 600;
  opacity: 0.95;
}

.hero-title {
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 700;
  max-width: 700px;
}

.hero-depart {
  letter-spacing: 0.12em;
  font-weight: 600;
}

.hero-visiting {
  max-width: 700px;
}

/* DAYS BADGE */
.days-badge {
  position: absolute;
  top: 18px;
  right: 22px;
  background: var(--gradient-blue);
  color: #ffffff;
  border-radius: 999px;
  padding: 10px 16px;
  text-align: center;
  min-width: 96px;
}

.days-number {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1;
}

.days-label {
  font-size: 0.75rem;
  text-transform: uppercase;
}

/* STATUS CHIP */
.status-chip {
  position: absolute;
  bottom: 14px;
  right: 22px;
}

/* FOOTER (single area, not a second card) */
.footer-wrapper {
  padding: 16px 24px 20px;
  background-color: #ffffff;
}

.footer-inner {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.footer-left {
  min-width: 0;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
}

.plan-btn {
  min-width: 220px;
}

/* Responsive */
@media (max-width: 959px) {
  .hero-img {
    height: 200px;
  }

  .footer-inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .footer-right {
    width: 100%;
  }

  .plan-btn {
    width: 100%;
  }
}
</style>
